import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { ActiveContext, Context, PersonProfile } from '@/types';
import { api } from '@/api/client';

interface AppContextValue {
  profile: PersonProfile | null;
  activeContext: ActiveContext | null;
  availableContexts: Context[];
  isLoading: boolean;
  setActiveContext: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppContextProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<PersonProfile | null>(null);
  const [activeContext, setActiveContextState] = useState<ActiveContext | null>(null);
  const [availableContexts, setAvailableContexts] = useState<Context[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [prof, active, contexts] = await Promise.all([
        api.me.profile().catch(() => null),
        api.contexts.getActive().catch(() => null),
        api.contexts.list().catch(() => []),
      ]);
      if (prof) setProfile(prof);
      setAvailableContexts(contexts);

      if (active) {
        setActiveContextState(active);
      } else if (contexts.length === 1) {
        // ONE CONTEXT SHOULD NEVER NEED CHOOSING.
        //
        // The sidebar's institution-admin link, the context banner and every scoped page
        // read `activeContext`, not `availableContexts` — so a person with exactly one
        // context and none active saw the bare Home/Products/Notifications shell and no
        // route to anything else. The switcher that would have fixed it is reached from a
        // control that only appears once there is more than one context to switch between.
        //
        // Failure is swallowed on purpose: an unreachable switch endpoint should leave the
        // page usable and the switcher available, not replace it with an error.
        try {
          setActiveContextState(await api.contexts.setActive(contexts[0].id));
        } catch {
          // left inactive; the switcher remains
        }
      } else {
        // MANY CONTEXTS, ONE WIDEST SEAT. A group administrator holds the group context
        // and one context per member school — and their home is the group's, where the
        // whole of what they administer is on one screen. So when exactly one group
        // context is on the table, it becomes the active seat without being asked; any
        // other multi-context person still chooses. Two group contexts stay ambiguous and
        // go to the switcher, same as before.
        const groupContexts = contexts.filter((c) => c.groupId);
        if (groupContexts.length === 1) {
          try {
            setActiveContextState(await api.contexts.setActive(groupContexts[0].id));
          } catch {
            // left inactive; the switcher remains
          }
        }
      }
    } catch {
      // degrade gracefully
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setActiveContext = useCallback(async (id: string) => {
    const ctx = await api.contexts.setActive(id);
    setActiveContextState(ctx);
  }, []);

  return (
    <AppContext.Provider value={{ profile, activeContext, availableContexts, isLoading, setActiveContext, refresh }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppContextProvider');
  return ctx;
}

/**
 * The same context, without the throw.
 *
 * For hooks that only use the context as a FALLBACK — vocabulary resolution takes
 * an explicit institution id and merely defaults to the active seat's. Such a hook
 * crashing an isolated mount (tests, storybook-style harnesses) over a provider it
 * did not need is a worse failure than quietly having no fallback.
 */
export function useOptionalAppContext(): AppContextValue | null {
  return useContext(AppContext);
}
